/**
 * Derived-node resolution machinery (EID-47).
 *
 * Pure data — no Solid / framework dependencies. The renderer (`node.tsx`)
 * calls `resolveDerived` on each render of a `derived` node to produce the
 * concrete subtree that should render in its place.
 *
 * Steps:
 *   1. Look up the source node by ID in the active spec.
 *   2. Deep-clone the source subtree.
 *   3. Rewrite IDs:
 *        - root of the clone → derived node's `id`
 *        - descendants       → prefixed with derived node's `id`
 *      so multiple derivations of the same source don't collide.
 *   4. Rewrite internal cross-references: any `x-reference`-tagged field
 *      whose value points to a descendant inside the cloned subtree is
 *      updated to the new (prefixed) ID. Refs targeting nodes outside
 *      the subtree are left untouched.
 *   5. Apply each JSON Patch in `patches` order to the rewritten clone.
 *      Patch paths must be **relative** to the derived subtree root —
 *      no leading slash. A leading-slash path (the JSON Pointer
 *      "absolute" form against the spec root) is reserved for future
 *      cross-subtree patches and is rejected in v1.
 *
 * Refuses to resolve when:
 *   - `derived` appears at the spec root (caller should reject earlier)
 *   - the source itself is a `derived` node (v1 restriction)
 *   - the source ID can't be found in the spec
 *   - a render-time cycle is detected (per-render visited set)
 */

import { walkReferences, type JsonSchema, type RefSite } from './x-reference';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface DerivedNode {
  id: string;
  nodeType: 'derived';
  source: string;
  patches?: PatchOp[][];
  /** Anything else the schema allows on a derived node. */
  [key: string]: unknown;
}

export interface PatchOp {
  op: 'add' | 'remove' | 'replace' | 'move' | 'copy' | 'test';
  path: string;
  value?: unknown;
  from?: string;
}

export interface DeriveOptions {
  /** Schemas keyed by short name (e.g. `node/world`). Used for ref-walk. */
  allSchemas?: Record<string, JsonSchema>;
  /** The schema that describes a generic node. Optional — without it the
   *  ref-walker silently no-ops on annotation-driven rewrites. */
  nodeSchema?: JsonSchema;
  /** Visited derived-node IDs on the current render path. Used for cycle
   *  detection so a chain `a -> b -> a` errors cleanly even if the v1
   *  derived-of-derived restriction is later relaxed. */
  visited?: Set<string>;
}

export class DerivedResolutionError extends Error {
  readonly code:
    | 'ROOT_DERIVED'
    | 'SOURCE_NOT_FOUND'
    | 'SOURCE_IS_DERIVED'
    | 'CYCLE'
    | 'DUPLICATE_ID'
    | 'PATCH_PATH_NOT_FOUND';
  readonly derivedId: string;
  constructor(
    code: DerivedResolutionError['code'],
    derivedId: string,
    message: string,
  ) {
    super(message);
    this.name = 'DerivedResolutionError';
    this.code = code;
    this.derivedId = derivedId;
  }
}

// ---------------------------------------------------------------------------
// Spec-load validation (cheap, does not materialise subtrees)
// ---------------------------------------------------------------------------

/**
 * True if `node` is shaped like a derived node. Used by the renderer to
 * dispatch to `resolveDerived`, and by spec-load static checks.
 */
export function isDerivedNode(node: unknown): node is DerivedNode {
  return (
    !!node &&
    typeof node === 'object' &&
    (node as { nodeType?: unknown }).nodeType === 'derived'
  );
}

/**
 * Static check: throws `DerivedResolutionError` if the spec root is a
 * derived node. Renderer should call this once at spec-load to surface the
 * error early without materialising any subtree.
 */
export function assertRootNotDerived(rootNode: unknown): void {
  if (isDerivedNode(rootNode)) {
    throw new DerivedResolutionError(
      'ROOT_DERIVED',
      (rootNode as DerivedNode).id ?? '<root>',
      'The root node of the spec cannot be a derived node',
    );
  }
}

// ---------------------------------------------------------------------------
// Source lookup — generic walk of the spec finding a node by id
// ---------------------------------------------------------------------------

/**
 * Find a node anywhere under `spec` matching `targetId`. Walks plain objects
 * and arrays generically — covers `children`, `tabs`, `items`, `layerSpec`,
 * `panels[n].node`, and any future container without enumeration.
 */
export function findNodeById(
  spec: unknown,
  targetId: string,
): Record<string, unknown> | null {
  if (spec == null || typeof spec !== 'object') return null;
  if (Array.isArray(spec)) {
    for (const item of spec) {
      const hit = findNodeById(item, targetId);
      if (hit) return hit;
    }
    return null;
  }
  const obj = spec as Record<string, unknown>;
  if (obj.id === targetId) return obj;
  for (const value of Object.values(obj)) {
    const hit = findNodeById(value, targetId);
    if (hit) return hit;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

/**
 * Resolve a derived node into the concrete subtree that should render in
 * its place. The returned object is a fresh clone — mutations to it do not
 * affect the source node in `spec`.
 *
 * Throws `DerivedResolutionError` on any failure path. Callers should
 * catch and surface the error to the user (the renderer renders an
 * inline error placeholder for v1).
 */
export function resolveDerived(
  derived: DerivedNode,
  spec: unknown,
  options: DeriveOptions = {},
): Record<string, unknown> {
  const visited = options.visited ?? new Set<string>();

  if (visited.has(derived.id)) {
    throw new DerivedResolutionError(
      'CYCLE',
      derived.id,
      `Cycle detected resolving derived node '${derived.id}' (chain: ${[...visited, derived.id].join(' -> ')})`,
    );
  }

  const source = findNodeById(spec, derived.source);
  if (!source) {
    throw new DerivedResolutionError(
      'SOURCE_NOT_FOUND',
      derived.id,
      `Derived node '${derived.id}' references unknown source '${derived.source}'`,
    );
  }
  if (isDerivedNode(source)) {
    throw new DerivedResolutionError(
      'SOURCE_IS_DERIVED',
      derived.id,
      `Derived node '${derived.id}' references another derived node '${derived.source}' as its source — not supported in v1`,
    );
  }

  // Deep-clone via JSON round-trip (the spec has no functions, Dates, etc).
  const cloned = deepClone(source);

  // Rewrite IDs and build oldId → newId map.
  const idMap = rewriteIds(cloned, derived.id, derived.id);

  // Rewrite internal cross-refs that target descendants of the source
  // subtree. External refs are left alone.
  if (options.nodeSchema) {
    const sites: RefSite[] = walkReferences(
      cloned,
      options.nodeSchema,
      options.allSchemas ?? {},
    );
    for (const site of sites) {
      const remapped = idMap.get(site.value);
      if (remapped) {
        setAtPath(cloned, site.path, remapped);
      }
    }
  }

  // Apply patches in order against the cloned subtree. Both leading-slash
  // ("/foo") and bare ("foo") paths resolve from the subtree root —
  // see applyPatchToSubtree for the full path-syntax contract.
  for (const patch of derived.patches ?? []) {
    applyPatchToSubtree(cloned, patch, derived.id);
  }

  return cloned;
}

// ---------------------------------------------------------------------------
// Internals — ID rewrite
// ---------------------------------------------------------------------------

/**
 * Replace the root id with `newRootId`, prefix every descendant id with
 * `newRootId + "."`, and return an oldId → newId map. Mutates the input
 * (caller should pre-clone).
 */
function rewriteIds(
  cloned: Record<string, unknown>,
  newRootId: string,
  derivedId: string,
): Map<string, string> {
  const idMap = new Map<string, string>();
  const oldRoot = cloned.id;
  if (typeof oldRoot === 'string') {
    idMap.set(oldRoot, newRootId);
    cloned.id = newRootId;
  }
  // Walk descendants
  for (const key of Object.keys(cloned)) {
    if (key === 'id') continue;
    walkRewriteDescendantIds(cloned[key], newRootId, idMap, derivedId);
  }
  return idMap;
}

function walkRewriteDescendantIds(
  value: unknown,
  prefix: string,
  idMap: Map<string, string>,
  derivedId: string,
): void {
  if (value == null) return;
  if (Array.isArray(value)) {
    for (const item of value)
      walkRewriteDescendantIds(item, prefix, idMap, derivedId);
    return;
  }
  if (typeof value !== 'object') return;

  const obj = value as Record<string, unknown>;
  const oldId = obj.id;
  if (typeof oldId === 'string' && oldId.length > 0) {
    if (idMap.has(oldId)) {
      // Two descendants share the same id. The reference walker can't
      // disambiguate them, so we'd end up rewriting some refs to the
      // wrong target. Fail loudly rather than producing a subtly wrong
      // render.
      throw new DerivedResolutionError(
        'DUPLICATE_ID',
        derivedId,
        `Source subtree of derived node '${derivedId}' contains duplicate descendant id '${oldId}' — ids must be unique within a subtree`,
      );
    }
    const newId = `${prefix}.${oldId}`;
    idMap.set(oldId, newId);
    obj.id = newId;
  }
  for (const v of Object.values(obj)) {
    walkRewriteDescendantIds(v, prefix, idMap, derivedId);
  }
}

// ---------------------------------------------------------------------------
// Internals — clone + path utilities
// ---------------------------------------------------------------------------

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/**
 * Set a value at a JSON-Pointer-ish dotted path within a subtree. Used by
 * the ref-rewrite step. Path format mirrors `walkReferences` output:
 * dot-segments for object keys, `[n]` for array indices.
 */
function setAtPath(
  root: Record<string, unknown>,
  path: string,
  value: unknown,
): void {
  const parts = parseDotBracketPath(path);
  if (parts.length === 0) return;
  let cur: unknown = root;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (cur === null || typeof cur !== 'object') return;
    cur =
      typeof part === 'number'
        ? (cur as unknown[])[part]
        : (cur as Record<string, unknown>)[part];
  }
  if (cur === null || typeof cur !== 'object') return;
  const last = parts[parts.length - 1];
  if (typeof last === 'number') {
    (cur as unknown[])[last] = value;
  } else {
    (cur as Record<string, unknown>)[last] = value;
  }
}

function parseDotBracketPath(path: string): (string | number)[] {
  const out: (string | number)[] = [];
  let i = 0;
  let buf = '';
  const flush = () => {
    if (buf) out.push(buf);
    buf = '';
  };
  while (i < path.length) {
    const ch = path[i];
    if (ch === '.') {
      flush();
      i++;
    } else if (ch === '[') {
      flush();
      const end = path.indexOf(']', i);
      if (end === -1) return out;
      out.push(Number(path.slice(i + 1, end)));
      i = end + 1;
      if (path[i] === '.') i++;
    } else {
      buf += ch;
      i++;
    }
  }
  flush();
  return out;
}

// ---------------------------------------------------------------------------
// Internals — patch application (subset of RFC 6902 needed by v1)
// ---------------------------------------------------------------------------

/**
 * Apply a single JSON Patch document to the resolved derived subtree.
 *
 * Per the EID-47 spec, derived patches always target the derived subtree
 * (the cloned, ID-rewritten projection of the source). They never reach
 * outside the subtree to mutate the spec — that would break the
 * "render-time projection" model.
 *
 * Path convention (RFC 6901 segment syntax, with subtree-relative roots):
 *   - "foo/bar" — no leading slash. The path is **relative** to the
 *     derived subtree root and is the only form supported in v1.
 *   - "/foo/bar" — leading slash is the JSON Pointer "absolute against
 *     the document root" form. For derived nodes this would mean
 *     "absolute against the spec root", which v1 doesn't implement.
 *     Reject with PATCH_PATH_NOT_FOUND so authors notice the convention
 *     mismatch immediately rather than silently mutating the wrong
 *     location.
 *
 * Supports add / remove / replace / test on object keys and array indices.
 * Move / copy are not implemented in v1 (rare in practice for derive use
 * cases) and will throw if encountered.
 */
function applyPatchToSubtree(
  subtree: Record<string, unknown>,
  patch: PatchOp[],
  derivedId: string,
): void {
  for (const op of patch) {
    applyPatchOp(subtree, op, derivedId);
  }
}

function applyPatchOp(
  subtree: Record<string, unknown>,
  op: PatchOp,
  derivedId: string,
): void {
  if (typeof op.path !== 'string') {
    throw new DerivedResolutionError(
      'PATCH_PATH_NOT_FOUND',
      derivedId,
      `Patch path is required and must be a string (op '${op.op}')`,
    );
  }
  if (op.path.startsWith('/')) {
    // EID-47 v1 only supports subtree-relative patches. A leading slash
    // is the JSON Pointer convention for "absolute against the document
    // root", which for derived nodes would mean spec-root absolute —
    // reserved for future cross-subtree patches. Reject so the convention
    // mismatch is visible immediately.
    throw new DerivedResolutionError(
      'PATCH_PATH_NOT_FOUND',
      derivedId,
      `Patch path '${op.path}' has a leading '/' — derived patches use subtree-relative paths in v1 (drop the leading slash)`,
    );
  }
  const parts = parseJsonPointerPath(op.path);
  if (parts.length === 0) {
    // Whole-subtree replace (rare). Only `replace` makes sense here, and
    // we deliberately don't handle it — the derived node itself describes
    // the source, and patches mutate the resolved subtree, not replace it
    // wholesale.
    throw new Error(
      `Patch operation '${op.op}' with empty path not supported on derived subtree`,
    );
  }
  if (op.op === 'move' || op.op === 'copy') {
    throw new Error(`Patch operation '${op.op}' is not supported in v1`);
  }
  const parent = walkToParent(subtree, parts);
  if (parent === null || typeof parent !== 'object') {
    // RFC 6902 requires an error when the target location for add /
    // replace / remove / test does not exist. Silently no-op'ing here
    // would let stale patches in the source spec invisibly produce a
    // wrong render — much harder to debug than a crisp error.
    throw new DerivedResolutionError(
      'PATCH_PATH_NOT_FOUND',
      derivedId,
      `Patch path '${op.path}' does not resolve in derived subtree of '${derivedId}'`,
    );
  }
  const last = parts[parts.length - 1];

  if (Array.isArray(parent)) {
    const idx = last === '-' ? parent.length : Number(last);
    if (Number.isNaN(idx) && last !== '-') {
      throw new DerivedResolutionError(
        'PATCH_PATH_NOT_FOUND',
        derivedId,
        `Patch path '${op.path}' has non-numeric array index '${last}' in derived subtree of '${derivedId}'`,
      );
    }
    switch (op.op) {
      case 'add':
        parent.splice(idx, 0, op.value);
        return;
      case 'remove':
        parent.splice(idx, 1);
        return;
      case 'replace':
        parent[idx] = op.value;
        return;
      case 'test': {
        const ok = JSON.stringify(parent[idx]) === JSON.stringify(op.value);
        if (!ok) {
          throw new Error(
            `Patch test op failed at '${op.path}': value did not match`,
          );
        }
        return;
      }
    }
  }

  const obj = parent as Record<string, unknown>;
  switch (op.op) {
    case 'add':
    case 'replace':
      obj[last] = op.value;
      return;
    case 'remove':
      delete obj[last];
      return;
    case 'test': {
      const ok = JSON.stringify(obj[last]) === JSON.stringify(op.value);
      if (!ok) {
        throw new Error(
          `Patch test op failed at '${op.path}': value did not match`,
        );
      }
      return;
    }
  }
}

function walkToParent(
  subtree: Record<string, unknown>,
  parts: (string | number)[],
): unknown {
  let cur: unknown = subtree;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (cur === null || typeof cur === 'undefined') return null;
    if (Array.isArray(cur)) {
      const idx = typeof part === 'number' ? part : Number(part);
      cur = cur[idx];
    } else if (typeof cur === 'object') {
      cur = (cur as Record<string, unknown>)[String(part)];
    } else {
      return null;
    }
  }
  return cur;
}

/**
 * Parse a derived-node patch path into RFC 6901 segments.
 *
 * Derived patches use subtree-relative paths (no leading slash). The
 * caller (applyPatchOp) rejects leading-slash paths before this function
 * is reached, so any `/` here is purely a segment separator.
 */
function parseJsonPointerPath(path: string): string[] {
  if (path === '') return [];
  return path
    .split('/')
    .map((seg) => seg.replace(/~1/g, '/').replace(/~0/g, '~'));
}
